import { inject, Injectable } from '@angular/core';
import { config } from '../../config/appConfig';
import { HttpService } from './http.service';
import { Observable } from 'rxjs';
import {
  IAuthorizedResponse,
  ICreateSessionRequest,
  IDeleteAccountResponse,
  IGetUserResponse,
  IRefreshTokenRequestBody,
  IRefreshTokenResponse,
  IRemoveDevicesRequest,
  IRevokeSessionsRequest,
  ISendInvoiceResponse,
  ISessionResponse,
  ISessionTokenRequest,
  ISetting,
  IUpdateUserInfo,
  IUpdateUserInfoResponse,
  IUserInfo,
} from '../models/user';
import { jwtDecode } from 'jwt-decode';
import { AdminEndPoints } from './admin.services';
import { HttpClient } from '@angular/common/http';
import {
  IAssignUserRoleRequest,
  ICreateRoleRequest,
  IFetchUserReportsRequest,
  IManagedRole,
  IMemberInfo,
  IPagesResponse,
  IPermissionDetail,
  IResponse,
  IRevokeUserRoleRequest,
  IRoleDetail,
  ISetRolePermissionsRequest,
  IUpdatePermissionRequest,
  IUpdateRoleRequest,
  IUserReportPagedResult,
  IUserRoleChangeResponse,
} from '../models/admin';
import {
  ICancelAutoRenewingSubscription,
  ICreateOrderRequest,
  IFulfillOrderRequest,
  IFulfillOrderResponse,
  IOrderInfo,
  IOrderInfoPagedResult,
  ISaveOrderResponse,
  ISubscriptionPlanWithTaxAndDiscountInfo,
  IUpdateSubscriptionRequest,
  OrderStatus,
  PaymentStatus,
  SubscriptionType,
} from '../models/Subscription';

/**
 * The largest page the members endpoint will serve.
 *
 * It refuses anything larger rather than clamping, so this is not a hint - a
 * request above it comes back as a validation error with no members at all.
 */
export const MEMBERS_PAGE_SIZE = 200;

export enum UserEndPoints {
  authorize = '/user/authorize',
  refreshToken = '/user/refreshToken',
  getUser = '/user',
  updateUser = '/user',
  subscriptionPlans = '/subscription/plans',
  order = '/order',
  allOrders = '/order/allorders',
  sendInvoice = '/order/SendInvoice',
  cancelOrder = '/order/cancel',
  sessionCreate = '/session',
  sessionRefresh = '/session/refresh',
  sessionValidate = '/session/validate',
  sessionRevoke = '/session/revoke',
  sessionRemoveDevices = '/session/removeDevices',
  sessionLogout = '/session/logout',
  orderFulfill = '/order/fulfill',
  subscriptionUpdate = '/subscription',
  userReports = '/user/userreports',
  roles = '/roles',
  members = '/roles/members',
  managedRoles = '/roles/managed',
  permissions = '/roles/permissions',
  permission = '/roles/permission',
  assignUserRole = '/roles/users/assign',
  revokeUserRole = '/roles/users/revoke',
}

@Injectable({
  providedIn: 'root',
})
export class UserService {
  httpService: HttpService = new HttpService(config.userServiceURL);
  userInfo: IUserInfo | null = null;

  // To be removed after getuser books api is available
  private http = inject(HttpClient);
  // isNewUser: boolean = true;

  // -------------------------------
  // This part will be removed post API change for checking new user
  decodedTokenInfo: IUserInfo | null = null;
  saveDecodedTokenInfo(token: string): void {
    let decodedInfo = this.decodeToken(token);

    if (decodedInfo && decodedInfo.UserInfo) {
      let userDecodeInformation = decodedInfo.UserInfo;

      // Map only properties that exist in IUserInfo
      this.decodedTokenInfo = {
        UserId: userDecodeInformation.UserId ?? null,
        Fullname: userDecodeInformation.Fullname ?? null,
        Email: userDecodeInformation.Email ?? null,
        Mobile: userDecodeInformation.Mobile ?? null,
        Gender: userDecodeInformation.Gender ?? null,
        Dob: userDecodeInformation.Dob ?? null,
        Grade: userDecodeInformation.Grade ?? null,
        MotherTongue: userDecodeInformation.MotherTongue ?? null,
        PreferredLanguage: userDecodeInformation.PreferredLanguage ?? null,
        GuardianName: userDecodeInformation.GuardianName ?? null,
        Address: userDecodeInformation.Address ?? null,
        PrimaryAddress: userDecodeInformation.PrimaryAddress ?? null,
        PictureUrl: userDecodeInformation.PictureUrl ?? null,
        PrimarySubscription: userDecodeInformation.PrimarySubscription ?? null,
        Declarations: userDecodeInformation.Declarations
          ? {
              IsAbove18: userDecodeInformation.Declarations.IsAbove18 ?? false,
              IsMarketingOptedIn:
                userDecodeInformation.Declarations.IsMarketingOptedIn ?? false,
            }
          : null,
        Roles: Array.isArray(userDecodeInformation.Roles)
          ? userDecodeInformation.Roles.map((role: any) => ({
              RoleName: role.RoleName ?? '',
              Permissions: Array.isArray(role.Permissions)
                ? role.Permissions
                : [],
            }))
          : [],
        Subscriptions: Array.isArray(userDecodeInformation.Subscriptions)
          ? userDecodeInformation.Subscriptions.map((sub: any) => ({
              Id: sub.Id ?? '',
              SubscriptionType: sub.SubscriptionType ?? '',
              Status: sub.Status ?? '',
              Rights: Array.isArray(sub.Rights) ? sub.Rights : [],
              AllowedUsers: sub.AllowedUsers ?? 0,
              StartDate: sub.StartDate ?? '',
              EndDate: sub.EndDate ?? '',
            }))
          : [],
      };
    }
  }
  decodeToken(token: string): any {
    try {
      return jwtDecode(token);
    } catch (error) {
      return null;
    }
  }

  getDecodedTokenInfo(): IUserInfo | null {
    return this.decodedTokenInfo;
  }
  // -------------------------------

  saveUserInfo(userInfo: IUserInfo) {
    this.userInfo = userInfo;
  }

  checkIfNewUser(): boolean {
    // The "email" and "mobile" are not required for new user(optional fields now)
    if (!this.userInfo?.Fullname || !this.userInfo.Declarations) {
      return true;
    }
    return false;
  }

  getUserInfo(): IUserInfo | null {
    return this.userInfo;
  }

  // Repository Services
  authorize(temptoken: string): Observable<IAuthorizedResponse> {
    return this.httpService.post(UserEndPoints.authorize);
  }

  // Refresh Token
  refreshToken(
    tokenData: IRefreshTokenRequestBody,
  ): Observable<IRefreshTokenResponse> {
    const reqBody: IRefreshTokenRequestBody = {
      Token: tokenData.Token,
      OtpUserInput: tokenData.OtpUserInput,
      OtpVerificationToken: tokenData.OtpVerificationToken,
      RefreshParentalControl: tokenData.RefreshParentalControl,
    };
    return this.httpService.post(UserEndPoints.refreshToken, reqBody);
  }

  // Get User
  getUser(
    userId: string,
    includeRoles: boolean = false,
  ): Observable<IGetUserResponse> {
    return this.httpService.get(
      UserEndPoints.getUser + '/' + userId + '?includeRoles=' + includeRoles,
    );
  }

  // Update User
  updateUser(
    userId: string,
    userInfo: IUpdateUserInfo,
  ): Observable<IUpdateUserInfoResponse> {
    return this.httpService.patch(
      UserEndPoints.updateUser + '/' + userId,
      userInfo,
    );
  }

  // Delete User Account
  deleteUserAccount(userId: string): Observable<IDeleteAccountResponse> {
    return this.httpService.delete(
      `${UserEndPoints.updateUser}/${userId}?useBundleId=false`,
    );
  }

  // Check If User Exists Or Not With Mobile/Email
  checkUserExistsByMobileOrEmail(
    mobileOrEmail: string,
  ): Observable<IResponse<boolean>> {
    return this.httpService.get(`/user/${mobileOrEmail}/exists`);
  }

  // Delete user mobile number
  deleteUserMobile(): Observable<IResponse<boolean>> {
    return this.httpService.delete(`/user/mobile`);
  }

  // Get Settings
  getSettings(): Observable<IResponse<ISetting[]>> {
    return this.httpService.get(`/settings`);
  }

  // Get User Books
  //Implementation Will Be Changed When Get User Books API is available
  getUserBooks(): Observable<any> {
    return this.http.get(
      `${config.adminServiceURL}${AdminEndPoints.EditorBooks}`,
    );
  }

  // Get Subscription Plans
  getSubscriptionPlans(): Observable<
    IResponse<ISubscriptionPlanWithTaxAndDiscountInfo[]>
  > {
    return this.httpService.get(UserEndPoints.subscriptionPlans, {
      autoCancelPendingOrder: true,
    });
  }

  // Create Order
  createOrder(
    order: ICreateOrderRequest,
  ): Observable<IResponse<ISaveOrderResponse>> {
    return this.httpService.post(UserEndPoints.order, order);
  }

  // Create Auto Renewing Order
  createAutoRenewOrder(
    order: ICreateOrderRequest,
    totalCycles?: number,
  ): Observable<IResponse<ISaveOrderResponse>> {
    return this.httpService.post(
      `${UserEndPoints.order}/autorenew${
        totalCycles != null ? `?totalCycles=${totalCycles}` : ''
      }`,
      order,
    );
  }

  // Cancel Auto Renewing Subscription
  // Stops future charges only; access continues until the paid-for end date.
  cancelAutoRenewingSubscription(
    request: ICancelAutoRenewingSubscription,
  ): Observable<IResponse<boolean>> {
    return this.httpService.post(
      `${UserEndPoints.order}/${request.orderId}/autorenew/cancel${
        request.cancelAtCycleEnd != null
          ? `?cancelAtCycleEnd=${request.cancelAtCycleEnd}`
          : ''
      }`,
      {},
    );
  }

  // Update order
  updateOrder(
    order: Partial<IOrderInfo> & { OrderId: string },
  ): Observable<IResponse<ISaveOrderResponse>> {
    return this.httpService.put(
      UserEndPoints.order + `/${order.OrderId}`,
      order,
    );
  }

  // Get All Orders
  getAllOrders(
    pageNumber: number,
    pageSize: number,
    orderStatus?: OrderStatus,
    paymentStatus?: PaymentStatus,
    search?: string,
    planid?: string,
    dateFrom?: string,
    dateTo?: string,
  ): Observable<IOrderInfoPagedResult> {
    const params: any = {
      pageNumber,
      pageSize,
      ...(orderStatus && { orderStatus }),
      ...(paymentStatus && { paymentStatus }),
      ...(search && { search }),
      ...(planid && { planid }),
      ...(dateFrom && { dateFrom }),
      ...(dateTo && { dateTo }),
    };
    return this.httpService.get(UserEndPoints.allOrders, params);
  }

  // Get User Reports
  getUserReports(
    request: IFetchUserReportsRequest,
  ): Observable<IUserReportPagedResult> {
    const params: IFetchUserReportsRequest = {
      PageNumber: request.PageNumber,
      PageSize: request.PageSize,
      ...(request.SubscriptionTypes && {
        SubscriptionTypes: request.SubscriptionTypes,
      }),
      ...(request.Search && { Search: request.Search }),
      ...(request.RegistrationDateFrom && {
        RegistrationDateFrom: request.RegistrationDateFrom,
      }),
      ...(request.RegistrationDateTo && {
        RegistrationDateTo: request.RegistrationDateTo,
      }),
    };
    return this.httpService.get(UserEndPoints.userReports, params);
  }

  // Get Order
  getOrder(orderId: string): Observable<IResponse<IOrderInfo>> {
    return this.httpService.get(UserEndPoints.order + `/${orderId}`, {
      autoCancelPendingOrder: true,
    });
  }

  // Get Payment Order
  getPaymentOrder(orderId: string): Observable<IResponse<ISaveOrderResponse>> {
    return this.httpService.get(
      UserEndPoints.order + `/${orderId}/paymentOrder`,
    );
  }

  // Send Invoice
  sendInvoice(orderId: string): Observable<IResponse<ISendInvoiceResponse>> {
    return this.httpService.post(UserEndPoints.sendInvoice, {
      OrderId: orderId,
    });
  }

  // Cancel Order
  cancelOrder(orderId: string): Observable<IResponse<boolean>> {
    return this.httpService.post(UserEndPoints.cancelOrder, {
      OrderId: orderId,
    });
  }

  // Session Remove Devices
  sessionRemoveDevices(
    devicesRequest: IRemoveDevicesRequest,
  ): Observable<IResponse<string>> {
    return this.httpService.post(
      UserEndPoints.sessionRemoveDevices,
      devicesRequest,
    );
  }

  // Session Revoke
  sessionRevoke(
    revokeSessionsRequest: IRevokeSessionsRequest,
  ): Observable<IResponse<string>> {
    return this.httpService.post(
      UserEndPoints.sessionRevoke,
      revokeSessionsRequest,
    );
  }

  // Session Validate
  sessionValidate(
    sessionTokenRequest: ISessionTokenRequest,
  ): Observable<IResponse<boolean>> {
    return this.httpService.post(
      UserEndPoints.sessionValidate,
      sessionTokenRequest,
    );
  }

  // Session Refresh
  sessionRefresh(
    sessionTokenRequest: ISessionTokenRequest,
  ): Observable<IResponse<ISessionResponse>> {
    return this.httpService.post(
      UserEndPoints.sessionRefresh,
      sessionTokenRequest,
    );
  }

  // Session Create
  sessionCreate(
    createSessionRequest: ICreateSessionRequest,
  ): Observable<IResponse<ISessionResponse>> {
    return this.httpService.post(
      UserEndPoints.sessionCreate,
      createSessionRequest,
    );
  }

  sessionLogout(): Observable<IResponse<string>> {
    return this.httpService.post(UserEndPoints.sessionLogout);
  }

  fulfillOrder(
    fulfillOrderRequest: IFulfillOrderRequest,
  ): Observable<IResponse<IFulfillOrderResponse>> {
    return this.httpService.post(
      UserEndPoints.orderFulfill,
      fulfillOrderRequest,
    );
  }

  subscriptionUpdate(
    subscriptionId: string,
    updateSubscriptionRequest: IUpdateSubscriptionRequest,
  ) {
    return this.httpService.put(
      UserEndPoints.subscriptionUpdate + '/' + subscriptionId,
      updateSubscriptionRequest,
    );
  }

  // Get All Roles (with permissions)
  getRoles(): Observable<IResponse<IRoleDetail[]>> {
    return this.httpService.get(UserEndPoints.roles);
  }

  // Get Members
  //
  // The default matches the server's cap. It refuses anything larger, so a
  // caller wanting the whole directory must page - see getAllMembers.
  getMembers(
    pageNumber: number = 1,
    pageSize: number = MEMBERS_PAGE_SIZE,
    search?: string,
    staffOnly: boolean = false,
  ): Observable<IPagesResponse<IMemberInfo>> {
    const params: any = {
      pageNumber,
      pageSize,
      staffOnly,
      ...(search && { search }),
    };
    return this.httpService.get(UserEndPoints.members, params);
  }

  /**
   * Every member, followed page by page.
   *
   * The endpoint caps pageSize at 200 and refuses anything larger outright, so
   * asking for one big page does not return a truncated list - it returns
   * nothing at all. Callers that need the whole directory follow TotalPages
   * instead, which also means the cap can be lowered later without this
   * breaking.
   */
  getAllMembers(staffOnly: boolean = false): Observable<IMemberInfo[]> {
    return new Observable<IMemberInfo[]>((observer) => {
      const collected: IMemberInfo[] = [];

      const fetchPage = (pageNumber: number): void => {
        this.getMembers(
          pageNumber,
          MEMBERS_PAGE_SIZE,
          undefined,
          staffOnly,
        ).subscribe({
          next: (response) => {
            collected.push(...(response.Value ?? []));

            const totalPages = response.TotalPages ?? 1;
            const current = response.PageNumber ?? pageNumber;

            if (current < totalPages) {
              fetchPage(current + 1);
            } else {
              observer.next(collected);
              observer.complete();
            }
          },
          error: (error) => observer.error(error),
        });
      };

      fetchPage(1);
    });
  }

  // ---------------------------------------------------------------------
  // Role, permission and staff management.
  //
  // All administrator-only. The server enforces that independently of the
  // UI - the tabs are hidden from anyone without the wildcard, but hiding a
  // tab is a courtesy, not a control, so every one of these is refused
  // server-side too.
  // ---------------------------------------------------------------------

  /**
   * Every user role, including the disabled ones.
   *
   * Distinct from getRoles, which fills the book-access picker and omits
   * disabled roles because there is no sense in granting a book to one.
   */
  getManagedRoles(): Observable<IResponse<IManagedRole[]>> {
    return this.httpService.get(UserEndPoints.managedRoles);
  }

  /** The whole permission catalogue, disabled rows included. */
  getAllPermissions(): Observable<IResponse<IPermissionDetail[]>> {
    return this.httpService.get(UserEndPoints.permissions);
  }

  createRole(request: ICreateRoleRequest): Observable<IResponse<IManagedRole>> {
    return this.httpService.post(UserEndPoints.roles, request);
  }

  /** Renames a role, enables it, or disables it. */
  updateRole(request: IUpdateRoleRequest): Observable<IResponse<IManagedRole>> {
    return this.httpService.put(UserEndPoints.roles, request);
  }

  /**
   * Replaces a role's permissions with the given set.
   *
   * The whole set rather than a delta, so what the screen shows is what gets
   * stored - a delta would leave the result depending on state the screen read
   * some time ago and may no longer be true.
   */
  setRolePermissions(
    request: ISetRolePermissionsRequest,
  ): Observable<IResponse<IManagedRole>> {
    return this.httpService.put(UserEndPoints.permissions, request);
  }

  /**
   * Updates a permission's description, or enables and disables it.
   *
   * The name is not sent because it cannot be changed: it is what joins the row
   * to the attributes in the code, so renaming it would detach the row and
   * silently revoke the permission everywhere.
   */
  updatePermission(
    request: IUpdatePermissionRequest,
  ): Observable<IResponse<IPermissionDetail>> {
    return this.httpService.put(UserEndPoints.permission, request);
  }

  assignUserRole(
    request: IAssignUserRoleRequest,
  ): Observable<IResponse<IUserRoleChangeResponse>> {
    return this.httpService.post(UserEndPoints.assignUserRole, request);
  }

  revokeUserRole(
    request: IRevokeUserRoleRequest,
  ): Observable<IResponse<IUserRoleChangeResponse>> {
    return this.httpService.post(UserEndPoints.revokeUserRole, request);
  }
}
